import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import './launchpad.css';
import Footer from '../../../customComponents/Footer';
import AuthService from '../../../api/services/AuthService';
import { resolvePublicUploadUrl } from '../../../api/apiConfig/apiConfig';

const LAUNCHPAD_LOGO_FALLBACK = '/images/AnnouncementImg/eul_coin.png';

const resolveLaunchpadImg = (url) => resolvePublicUploadUrl(url) || LAUNCHPAD_LOGO_FALLBACK;

const formatLaunchpadTime = (value) => {
    if (!value) return '--';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const mapLaunchpadStatus = (status) => {
    switch (String(status || '').toUpperCase()) {
        case 'LIVE': return 'Ongoing';
        case 'UPCOMING': return 'Upcoming';
        case 'ENDED': return 'Ended';
        default: return 'Ended';
    }
};

const normalizeLaunchpad = (item) => {
    const tokenSymbol = item?.tokenSymbol || item?.baseCurrency?.short_name || 'Token';
    const acceptedCurrency = item?.acceptedCurrency || item?.quoteCurrency?.short_name || 'USDT';
    const tokenPrice = item?.tokenPrice ?? 0;
    const participants = (item?.totalParticipants ?? 0).toLocaleString();
    const subPrice = `1 ${tokenSymbol} = ${tokenPrice} ${acceptedCurrency}`;
    return {
        id: item?._id,
        logo: tokenSymbol,
        logoUrl: resolveLaunchpadImg(
            item?.baseCurrency?.icon_path || item?.baseCurrency?.iconPath || item?.logo
        ),
        name: tokenSymbol,
        status: mapLaunchpadStatus(item?.status),
        subscriptionPrice: [subPrice],
        participants,
        eventTime: formatLaunchpadTime(item?.startTime),
        pools: [
            {
                name: acceptedCurrency,
                coinIcon: acceptedCurrency,
                allocation: (item?.totalSupply ?? 0).toLocaleString(),
                allocationCoin: tokenSymbol,
                commitment: (item?.totalRaised ?? 0).toLocaleString(),
                commitmentCoin: acceptedCurrency,
                cap: (item?.maxBuy ?? 0).toLocaleString(),
                capCoin: acceptedCurrency,
                subPrice,
                poolParticipants: participants
            }
        ]
    };
};

const poolIconMap = {
    'USD1': 'usd_commit_icon.svg',
    'GUSD': 'usd_commit_icon2.svg',
    'USDT': 'usd_commit_icon3.svg'
};

const Launchpad = () => {
    const navigate = useNavigate();
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [activeTab, setActiveTab] = useState("Ongoing");
    const [visibleCount, setVisibleCount] = useState(3);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [projects, setProjects] = useState([]);
    const loaderRef = React.useRef(null);

    const ongoingCount = projects.filter(p => p.status === 'Ongoing').length;
    const upcomingCount = projects.filter(p => p.status === 'Upcoming').length;
    const endedCount = projects.filter(p => p.status === 'Ended').length;

    useEffect(() => {
        let isMounted = true;
        const fetchLaunchpads = async () => {
            try {
                const result = await AuthService.getLaunchpads();
                if (!isMounted) return;
                if (result?.success && Array.isArray(result?.data)) {
                    setProjects(result.data.map(normalizeLaunchpad));
                } else {
                    setProjects([]);
                }
            } catch (err) {
                if (isMounted) setProjects([]);
            }
        };
        fetchLaunchpads();
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        setVisibleCount(3);
    }, [activeTab]);

    useEffect(() => {
        const handleScroll = () => {
            const isBottom = window.innerHeight + document.documentElement.scrollTop + 100 >= document.documentElement.offsetHeight;
            const currentFilteredCount = projects.filter(p => p.status === activeTab).length;
            if (isBottom && !isLoadingMore && visibleCount < currentFilteredCount) {
                setIsLoadingMore(true);
                setTimeout(() => {
                    setVisibleCount(prevCount => prevCount + 3);
                    setIsLoadingMore(false);
                }, 1000);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isLoadingMore, visibleCount, activeTab, projects]);

    return (
        <>

            <Helmet>
                <title>AGCX Launchpad | Invest in New Crypto Token Launches</title>
                <meta
                    name="description"
                    content="Get early access to vetted token launches on AGCX Launchpad. A secure IEO platform connecting retail and institutional investors to new projects."
                />
                <meta name="keywords" content="crypto launchpad, IEO platform, new token launch UAE" />
                <link rel="canonical" href="https://arabglobal.ae/launchpad" />
            </Helmet>

            <div className="launchpad_wrapper_main">
                <div className="container">
                    <div className="launchpad_cnt_block">
                        <div className='launchpad_left_cnt'>

                            <h1>Launchpad</h1>
                            <p>Be Early to the Next Big Token Project</p>
                            {/* <ul>
                            <li>$2.56B<span>Amount</span></li>
                            <li>110.09K<span>Participants</span></li>
                        </ul> */}
                            <div className='about_Launchpad_btn d-flex gap-3'>
                                <button className='aboutbtn_launchpad' onClick={() => setShowAboutModal(true)}>About Launchpad</button>
                                {/* <button className='notification_btn'><i className="ri-notification-3-fill"></i></button> */}
                            </div>

                        </div>

                        <div className='launchpad_right_cnt'>
                            <img src="images/launchpad-img/launchpad_acge.png" alt="launchpad_acge" />
                        </div>

                    </div>

                    <div className="launchpad_past_projects_sec mt-5">
                        {/* <h2 className="past_projects_title">Past Projects</h2> */}

                        <div className="launchpad_projects_tabs">
                            <button
                                className={`projects_tab ${activeTab === 'Ongoing' ? 'active' : ''}`}
                                onClick={() => setActiveTab('Ongoing')}
                            >
                                Ongoing ({ongoingCount})
                            </button>
                            <button
                                className={`projects_tab ${activeTab === 'Upcoming' ? 'active' : ''}`}
                                onClick={() => setActiveTab('Upcoming')}
                            >
                                Upcoming ({upcomingCount})
                            </button>
                            <button
                                className={`projects_tab ${activeTab === 'Ended' ? 'active' : ''}`}
                                onClick={() => setActiveTab('Ended')}
                            >
                                Ended ({endedCount})
                            </button>
                        </div>

                        {projects.filter(project => project.status === activeTab).length === 0 && (
                            <div className="no_projects_found">
                                <img src="/images/no-data.svg" className="launchpad_empty_img" alt="no-data" />
                                <span>No projects right now.</span>
                            </div>
                        )}

                        {projects.filter(project => project.status === activeTab).slice(0, visibleCount).map(project => (
                            <div className="project_card" key={project.id}>
                                <div className="project_card_status">{project.status}</div>
                                <div className="project_card_inner">
                                    <div className="project_card_left" onClick={() => navigate(`/launchpad-detail/${project.id}`)}>
                                        <div className="project_title_sec">
                                            <div className={`project_icon ${project.logo.toLowerCase()}`}>
                                                <img src={project.logoUrl} alt={project.name} onError={(e) => { e.target.src = LAUNCHPAD_LOGO_FALLBACK; }} />
                                            </div>
                                            <div className="project_name_badge">
                                                <h3>{project.name}</h3>
                                                {project.badge && <span className="initial_badge">{project.badge}</span>}
                                            </div>
                                        </div>

                                        <div className="project_info_group">
                                            <p className="info_label">Subscription Price</p>
                                            <div className="info_value">
                                                {project.subscriptionPrice.map((price, idx) => <p key={idx}>{price}</p>)}
                                            </div>
                                        </div>

                                        <div className="project_info_group">
                                            <p className="info_label">Number of Participants</p>
                                            <div className='info_value'>
                                                <p className="info_value">{project.participants}</p>
                                            </div>
                                        </div>

                                        <div className="project_info_group mt-auto">
                                            <p className="info_label">Event Time</p>
                                            <div className='info_value'>
                                                <p className="info_value">{project.eventTime}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="project_card_right">
                                        {project.pools.map((pool, idx) => (
                                            <div className="pool_card" key={idx} onClick={() => navigate(`/launchpad-detail/${project.id}`)}>
                                                <div className="pool_title">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className={`pool_icon ${pool.coinIcon.toLowerCase()}`}>
                                                            <img src={`images/launchpad-img/${poolIconMap[pool.coinIcon] || 'usd_commit_icon.svg'}`} alt={pool.coinIcon} />
                                                        </div>
                                                        <h4>{pool.name}</h4>
                                                    </div>
                                                    {pool.thresholdText && <div className="threshold_text">{pool.thresholdText}</div>}
                                                </div>

                                                <div className="pool_stats_row">
                                                    <div className="pool_stat">
                                                        <p className="stat_label">Allocation</p>
                                                        <p className="stat_value">{pool.allocation} <span>{pool.allocationCoin}</span></p>
                                                    </div>
                                                    <div className="pool_stat text-end">
                                                        <p className="stat_label">Commitment</p>
                                                        <p className="stat_value">{pool.commitment} <span>{pool.commitmentCoin}</span></p>
                                                    </div>
                                                </div>

                                                <div className="pool_stat mt-3">
                                                    <p className="stat_label">{pool.capLabel || 'Cap per Subscriber'}</p>
                                                    <p className="stat_value">{pool.cap} <span>{pool.capCoin}</span></p>
                                                </div>

                                                <div className="pool_summary mt-4">
                                                    <div className="summary_row">
                                                        <p className="summary_label">Subscription Price</p>
                                                        <p className="summary_value">{pool.subPrice}</p>
                                                    </div>
                                                    <div className="summary_row">
                                                        <p className="summary_label">Number of Participants</p>
                                                        <p className="summary_value">{pool.poolParticipants}</p>
                                                    </div>
                                                </div>

                                                <button className="trade_btn" onClick={(e) => { e.stopPropagation(); navigate(`/launchpad-detail/${project.id}`); }}>Trade</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {isLoadingMore && (
                            <>
                                {[1, 2].map((idx) => (
                                    <div className="project_card skeleton_card" key={`skel-${idx}`}>
                                        <div className="project_card_status" style={{ width: '80px', height: '24px', background: '#e0e0e0', borderRadius: '4px' }}></div>
                                        <div className="project_card_inner">
                                            <div className="project_card_left">
                                                <div className="project_title_sec">
                                                    <div className="project_icon" style={{ width: '40px', height: '40px', background: '#e0e0e0', borderRadius: '50%' }}></div>
                                                    <div className="project_name_badge">
                                                        <div style={{ width: '100px', height: '20px', background: '#e0e0e0', borderRadius: '4px' }}></div>
                                                    </div>
                                                </div>
                                                <div className="project_info_group" style={{ marginTop: '20px' }}>
                                                    <div style={{ width: '120px', height: '15px', background: '#e0e0e0', borderRadius: '4px', marginBottom: '8px' }}></div>
                                                    <div style={{ width: '80px', height: '15px', background: '#e0e0e0', borderRadius: '4px' }}></div>
                                                </div>
                                            </div>
                                            <div className="project_card_right">
                                                <div className="pool_card">
                                                    <div className="pool_title">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="pool_icon" style={{ width: '30px', height: '30px', background: '#e0e0e0', borderRadius: '50%' }}></div>
                                                            <div style={{ width: '80px', height: '15px', background: '#e0e0e0', borderRadius: '4px' }}></div>
                                                        </div>
                                                    </div>
                                                    <div className="pool_stats_row" style={{ marginTop: '20px' }}>
                                                        <div style={{ width: '100%', height: '40px', background: '#e0e0e0', borderRadius: '4px' }}></div>
                                                    </div>
                                                    <div style={{ width: '100%', height: '40px', background: '#e0e0e0', borderRadius: '4px', marginTop: '10px' }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    {/* <div className="view_more_sec">
                    <Link to="#" className="view_more_link">View More</Link>
                </div> */}
                </div>

                {
                    showAboutModal && (
                        <div className="launchpad_modal_overlay">
                            <div className="launchpad_modal_content">
                                <div className="launchpad_modal_header">
                                    <h2>About Launchpad</h2>
                                    <button className="close_modal_btn" onClick={() => setShowAboutModal(false)}>
                                        <i className="ri-close-line"></i>
                                    </button>
                                </div>
                                <div className="launchpad_modal_body mt-4">
                                    <div className="faq_content">
                                        <div className="faq_item">
                                            <h5>1. What is Launchpad?</h5>
                                            <p>AGCX Launchpad is where you can subscribe to new token sales listed on AGCX. Eligible users lock or commit supported assets during the event window to receive project token allocations according to each campaign’s rules.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>2. How can I participate in Launchpad?</h5>
                                            <p>Log in to your AGCX account, open Launchpad, choose an active project, and follow the on-screen steps. Complete KYC if required. Allocation is subject to each project’s eligibility, cap, and trading-volume rules.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>3. Why are there staking caps in Launchpad? How to increase them?</h5>
                                            <p>AGCX sets participation caps so allocations stay fair. Caps may be based on your recent AGCX spot and futures volume. Higher verified volume can unlock a higher cap. Exact tiers are shown on each project page.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>4. Which coins can I stake in Launchpad pool?</h5>
                                            <p>Supported assets are listed on each AGCX Launchpad project page (for example USDT or other announced tokens). Always check the live project details before you commit funds.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>5. How to get airdrop bonuses?</h5>
                                            <p>If a campaign includes extra AGCX Launchpad rewards, they are credited only when you meet that project’s rules (such as minimum stake or trading volume). Details are published on the project page and in AGCX announcements.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>6. How many tokens will be rewarded?</h5>
                                            <p>Your allocation depends on your committed amount versus the pool, plus any hard cap. AGCX snapshots participation during the event. Final amounts appear in your AGCX Launchpad history after distribution.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>7. Why didn't I receive a bonus after subscribing?</h5>
                                            <p>You may have already hit the campaign cap, missed a snapshot, or not met the minimum requirement. Check the project rules on AGCX Launchpad or contact support@arabglobal.ae.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>8. How are rewards distributed?</h5>
                                            <p>After the AGCX Launchpad event ends, eligible tokens are credited to your AGCX spot wallet according to the project timeline shown on the campaign page.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>9. When will the staked assets be redeemed?</h5>
                                            <p>Committed assets return to your AGCX spot wallet after the lock or subscription period ends, unless the project states otherwise. Early exit, if allowed, may forfeit rewards.</p>
                                        </div>
                                        <div className="faq_item">
                                            <h5>10. Can I redeem my staked assets at any time?</h5>
                                            <p>Only if the specific AGCX Launchpad campaign allows early redemption. Early exit can reduce or cancel rewards. Follow the project terms on the Launchpad page.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }

            </div >
            <Footer />

        </>

    );
};

export default Launchpad;
